import { format, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';

export function parseTimeToMins(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function minsToTime(mins: number): string {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Convert a local datetime input string ('YYYY-MM-DDTHH:mm') to a UTC ISO string
 * for storage. The input is treated as UTC wall-clock time (no offset applied).
 */
export function localInputToISO(s: string): string {
  return new Date(s + 'Z').toISOString();
}

/**
 * Prepare a stored UTC ISO string for use in a datetime input / DatePicker.
 * Returns 'YYYY-MM-DDTHH:mm' using the UTC components of the stored value.
 */
export function isoToLocalInput(iso: string): string {
  return iso.slice(0, 16);
}

/**
 * Format a UTC ISO string as 'HH:mm' without local-timezone conversion.
 */
export function fmtUTCTime(iso: string): string {
  return iso.slice(11, 16);
}

/**
 * Format a UTC ISO string as date + time without local-timezone conversion.
 * Example: fmtUTCDateTime('2026-09-20T13:00:00.000Z', 'MMM d') → 'Sep 20, 13:00'
 */
export function fmtUTCDateTime(iso: string, datePat = 'MMM d', locale?: Locale): string {
  return `${format(parseISO(iso.slice(0, 10)), datePat, { locale })}, ${iso.slice(11, 16)}`;
}

/**
 * Format a UTC ISO string as date only without local-timezone conversion.
 * Safe for any date pattern — extracts the date part before parsing.
 */
export function fmtUTCDate(iso: string, datePat = 'MMM d, yyyy', locale?: Locale): string {
  return format(parseISO(iso.slice(0, 10)), datePat, { locale });
}

/** `YYYY-MM-DD` for the device's local date. */
export function localDateKey(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd');
}

/**
 * The trip day happening on `todayKey`, or null when the trip has not started
 * or is already over. A Trip stores plain calendar dates with no timezone of
 * its own (`@db.Date`), so the device's date is the only possible "today".
 * `todayKey` is a parameter and not a `new Date()` inside so this stays pure
 * and testable without touching the system clock.
 */
export function currentTripDay<T extends { date: string }>(
  days: T[],
  todayKey: string = localDateKey(),
): T | null {
  return days.find(d => d.date.slice(0, 10) === todayKey) ?? null;
}

/**
 * The day the itinerary should open on: the one being lived while the trip is
 * running, day 1 otherwise — a finished trip is reread from the start and a
 * future one is planned from the start.
 */
export function initialItineraryDay<T extends { date: string }>(
  days: T[],
  todayKey: string = localDateKey(),
): T | null {
  return currentTripDay(days, todayKey) ?? days[0] ?? null;
}
